import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { compact, isEmpty } from 'lodash-es';
import { useIntl } from 'react-intl';

import { Drawer } from 'components/drawer';
import Modal, { ModalSize } from 'components/modal';
import { Calories } from 'components/price-and-calories';
import { ResponsiveCarousel } from 'components/responsive-carousel';
import { ItemAvailabilityStatus } from 'enums/menu';
import { BenefitType, IBenefitSwap } from 'generated/graphql-gateway';
import { IItemFragment, useAllItemsQuery } from 'generated/sanity-graphql';
import usePosVendor from 'hooks/menu/use-pos-vendor';
import { useFormatCalories } from 'hooks/use-format-calories';
import useIsComponentMounted from 'hooks/use-is-component-mounted';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { useLoyaltyContext } from 'state/loyalty';
import { EventTypes, useMParticleContext } from 'state/mParticle';
import { useMenuContext } from 'state/menu';
import { useOrderContext } from 'state/order';
import { useStoreContext } from 'state/store';
import { primitive } from 'styles/constants/primitives';
import { isMobile } from 'utils/environment';

import {
  ButtonsContainer,
  CloseButton,
  Container,
  Header,
  ItemImage,
  MainTitle,
  NoThanks,
  SingleItem,
  SingleItemContent,
  StyledPointsIcon,
  UpsizeButton,
} from './modal-upsize.styled';

const ModalUpsize = () => {
  const [availableItems, setAvailableItems] = useState<IItemFragment[]>();
  const [modalVisible, setModalVisible] = useState(true);
  const { loyaltySurpriseOffersEnabled } = useLoyaltyContext();
  const upsizeAvailable = useAppSelector(selectors.loyalty.selectUpsizeAvailable);
  const userOffers = useAppSelector(selectors.loyalty.selectUserOffers);
  const dispatch = useAppDispatch();
  const { swapItems } = useOrderContext();
  const isMounted = useIsComponentMounted();
  const { checkItemAvailability } = useMenuContext();
  const { vendor } = usePosVendor();
  const { store } = useStoreContext();
  const formatCalories = useFormatCalories();
  const { logRBIEvent } = useMParticleContext();

  const addr = store.physicalAddress;

  const restaurantAddress = useMemo(
    () => ({
      restaurantId: store._id || '',
      restaurantAddress: compact([addr?.address1, addr?.address2]).join(', ') || '',
      restaurantZip: addr?.postalCode || '',
      restaurantCity: addr?.city || '',
      restaurantState: addr?.stateProvince || '',
      restaurantCountry: addr?.country || '',
    }),
    [addr, store._id]
  );

  const resetUpsizeAvailability = () => dispatch(actions.loyalty.resetUpsizeAvailability());

  const swappableItemsMap = userOffers.reduce((acc, offer) => {
    if (offer.benefits?.length) {
      offer.benefits.forEach((benefit: IBenefitSwap) => {
        if (benefit.type === BenefitType.SWAP) {
          acc[benefit.value.to] = {
            ...benefit.value,
            offerId: offer.id,
            offerType: offer.type,
            cmsId: offer.sanityId,
          };
        }
      });
    }

    return acc;
  }, {});

  const { loading, data } = useAllItemsQuery({
    variables: {
      where: {
        _id_in: Object.keys(swappableItemsMap),
      },
    },
    skip: isEmpty(swappableItemsMap),
  });

  const [swapTo, setSelectedSwapTo] = useState<{ [key: string]: any }>({});
  const { formatMessage } = useIntl();
  const modalHeading = formatMessage({ id: 'freeRewardsUpsize' });

  const logUpsizeDisplayed = useCallback(
    (itemNames: string) => {
      logRBIEvent({
        name: 'Upsize Displayed',
        type: EventTypes.Other,
        attributes: {
          eligibleProductsName: itemNames,
          ...restaurantAddress,
        },
      });
    },
    [logRBIEvent, restaurantAddress]
  );

  useEffect(() => {
    if (!availableItems || availableItems?.length === 0) {
      return;
    }
    const itemNames = availableItems
      .map(item => item.name?.locale || '')
      .filter(name => name !== '');
    logUpsizeDisplayed(itemNames.join(', '));
  }, [availableItems, logUpsizeDisplayed]);

  useEffect(() => {
    if (availableItems?.length) {
      setSelectedSwapTo(availableItems[0]);
    }
  }, [availableItems, data, setSelectedSwapTo]);

  useEffect(() => {
    (async () => {
      let filteredAvailableItems = [];

      if (data && data.allItems) {
        for (const itemToCheck of data.allItems) {
          const { availabilityStatus } = await checkItemAvailability({
            vendor,
            restaurantPosDataId: store.restaurantPosData?._id ?? '',
            storeNumber: store.number ?? '',
            itemId: `${itemToCheck._type}-${itemToCheck._id}`,
          });

          if (availabilityStatus === ItemAvailabilityStatus.AVAILABLE) {
            filteredAvailableItems.push(itemToCheck);
          }
        }
        setAvailableItems(filteredAvailableItems);
      }
    })();
  }, [checkItemAvailability, data, store.number, store.restaurantPosData, vendor]);

  const hideModal = !upsizeAvailable || loading || !availableItems?.length || !isMounted;

  if (!loyaltySurpriseOffersEnabled || hideModal) {
    return null;
  }

  const logUpsizeSelection = (itemName: string) => {
    logRBIEvent({
      name: 'Upsize Selection',
      type: EventTypes.Other,
      attributes: {
        selectedProductName: itemName,
        ...restaurantAddress,
      },
    });
  };

  const onUpsizeClickHandler = () => {
    const { from: swapFrom, ...selectedOffer } = swappableItemsMap[swapTo._id];
    swapItems(swapFrom, swapTo, selectedOffer);
    resetUpsizeAvailability();
    logUpsizeSelection(swapTo.name?.locale || '');
  };

  const UpsizeContent = () => {
    return (
      <Container>
        <Header>
          <StyledPointsIcon />
          <MainTitle>{formatMessage({ id: 'upsizeForFree' })}</MainTitle>
          <span> {formatMessage({ id: 'upsizeEligible' })}</span>
        </Header>
        <div>
          {availableItems && availableItems?.length > 0 && (
            <ResponsiveCarousel centerCards={true}>
              {availableItems.map((cmsItem: IItemFragment) => {
                const { _id, image, name, nutrition } = cmsItem;
                const caloriesRounded = formatCalories(nutrition?.calories || null);

                return (
                  <SingleItem
                    onClick={() => setSelectedSwapTo(cmsItem)}
                    key={_id}
                    className={swapTo._id === _id ? 'selected' : ''}
                  >
                    <SingleItemContent>
                      {image && <ItemImage alt={name?.locale || ''} image={image} />}
                      <div>
                        <h4>{name?.locale}</h4>
                        {caloriesRounded && (
                          <Calories>
                            <p>{caloriesRounded}</p>
                          </Calories>
                        )}
                      </div>
                    </SingleItemContent>
                  </SingleItem>
                );
              })}
            </ResponsiveCarousel>
          )}
        </div>
        <ButtonsContainer>
          <UpsizeButton
            disabled={loading || !data}
            onClick={onUpsizeClickHandler}
            aria-label="upsize-btn"
            data-testid="upsize-modal-accept-button"
          >
            {formatMessage({ id: 'freeUpsize' })}
          </UpsizeButton>
          <NoThanks
            onClick={resetUpsizeAvailability}
            aria-label={formatMessage({ id: 'noThanks' })}
          >
            {formatMessage({ id: 'noThanks' })}
          </NoThanks>
        </ButtonsContainer>
      </Container>
    );
  };

  const handleDismissDrawer = () => {
    setModalVisible(false);
    setTimeout(() => {
      resetUpsizeAvailability();
    }, 450);
  };

  return isMobile() ? (
    <Drawer
      data-testid="upsize-drawer"
      allowsDismiss
      onDismiss={handleDismissDrawer}
      isVisible={modalVisible}
      mParticleEventData={{
        modalAppearanceEventMessage: 'Upsize available',
        modalMessage: modalHeading,
      }}
    >
      <UpsizeContent />
    </Drawer>
  ) : (
    <Modal
      data-testid="upsize-modal"
      allowsDismiss
      backgroundColor={primitive.$white}
      closeButton={() => <CloseButton onClick={resetUpsizeAvailability} />}
      onDismiss={resetUpsizeAvailability}
      mParticleEventData={{
        modalAppearanceEventMessage: 'Upsize available',
        modalMessage: modalHeading,
      }}
      size={ModalSize.AUTO}
    >
      <UpsizeContent />
    </Modal>
  );
};

export default ModalUpsize;
